import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { getTableRowHoverOverlay } from '../../../../styles/theme/tableRowOverlays';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Avatar,
  InputAdornment,
  Popper,
  CircularProgress,
  IconButton,
  Switch,
  Divider,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Store as StoreIcon,
  SwapHoriz as ExchangeIcon,
} from '@mui/icons-material';
import {
  SalesOrder,
  ExchangeableItem,
  ExchangeFormData,
  ExchangeSummary,
  SelectedProduct,
  WarehouseInventoryProduct,
} from '../../../../types/sales.types';
import { DashboardCard } from './ExchangeDrawer.styles';
import { useExchangeProductSearch } from './useExchangeProductSearch';
import {
  getExchangeCreditStep1ReminderMessage,
  isExchangeSummaryInvalid,
} from './exchangePricing.utils';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import type { SalesCatalogScope } from '../../../sales/SalesStep1/SalesStep1.hooks';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import RemoteOriginChip from '../../../sales/shared/RemoteOriginChip';
import ProductImagePreview, { ProductImageData } from '../../../sales/shared/ProductImagePreview';
import * as S from './ExchangeStep1Selection.styles';

const getProductImageData = (product: ProductImageData): ProductImageData => ({
  thumbnail: product.thumbnail || null,
  thumbnailUrl: product.thumbnailUrl || null,
  imagePath: product.imagePath || null,
});

interface ExchangeStep1SelectionProps {
  order: SalesOrder;
  eligibility: ExchangeableItem[];
  exchangeData: ExchangeFormData;
  onSelectItemsToExchange: (itemIds: number[]) => void;
  onAddNewProduct: (product: WarehouseInventoryProduct) => void;
  onRemoveNewProduct: (productId: number) => void;
  onClearNewProducts: () => void;
  onUpdateNewProduct: (productId: number, updates: Partial<SelectedProduct>) => void;
  onUpdateExchangeNotes: (notes: string) => void;
  exchangeSummary: ExchangeSummary;
  formatCurrency: (amount: number) => string;
}

const ExchangeStep1Selection: React.FC<ExchangeStep1SelectionProps> = ({
  order,
  eligibility,
  exchangeData,
  onSelectItemsToExchange,
  onAddNewProduct,
  onRemoveNewProduct,
  onClearNewProducts,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdateNewProduct,
  onUpdateExchangeNotes,
  exchangeSummary,
  formatCurrency,
}) => {
  const { canMixedSell } = useOrdersPermissions();
  const [catalogScope, setCatalogScope] = useState<SalesCatalogScope>('selling_branch');

  const {
    searchQuery,
    showDropdown,
    filteredProducts,
    isLoadingProducts,
    isLoadingMoreProducts,
    anchorEl,
    searchInputRef,
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleDropdownScroll,
    resetSearchAfterSelect,
  } = useExchangeProductSearch(order.location?.id, exchangeData, catalogScope, canMixedSell);

  useEffect(() => {
    if (!canMixedSell && catalogScope === 'all_branches') {
      setCatalogScope('selling_branch');
    }
  }, [canMixedSell, catalogScope]);

  const handleMixedSaleToggle = useCallback(
    (value: SalesCatalogScope) => {
      if (value === catalogScope) return;
      if (exchangeData.newProducts.length > 0) {
        onClearNewProducts();
      }
      setCatalogScope(value);
    },
    [catalogScope, exchangeData.newProducts.length, onClearNewProducts],
  );

  const handleProductSelect = useCallback(
    (product: WarehouseInventoryProduct) => {
      onAddNewProduct(product);
      resetSearchAfterSelect();
    },
    [onAddNewProduct, resetSearchAfterSelect],
  );

  const handleToggleItem = (itemId: number) => {
    const eligibilityItem = eligibility.find((item) => item.itemId === itemId);
    if (eligibilityItem && !eligibilityItem.canExchange) {
      return;
    }
    const currentSelection = exchangeData.itemsToExchange;
    if (currentSelection.includes(itemId)) {
      onSelectItemsToExchange(currentSelection.filter((id) => id !== itemId));
    } else {
      onSelectItemsToExchange([...currentSelection, itemId]);
    }
  };

  const exchangeSummaryInvalid = isExchangeSummaryInvalid(
    exchangeSummary.newItemsTotal,
    exchangeSummary.paidForExchangedItems,
    exchangeSummary.isSummaryCoherent,
  );

  const selectedOutgoingItems = useMemo(
    () => eligibility.filter((item) => exchangeData.itemsToExchange.includes(item.itemId)),
    [eligibility, exchangeData.itemsToExchange],
  );

  const totalSelectedPieces = selectedOutgoingItems.length + exchangeData.newProducts.length;

  return (
    <S.MainContainer>
      <S.LeftColumn>
        {/* KPIs */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(124, 93, 201, 0.16)',
                      color: '#7C5DC9',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <ShoppingCartIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4, color: '#7C5DC9' }}>
                      Piezas a intercambiar
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: '#7C5DC9', lineHeight: 1.2 }}
                    >
                      {formatCurrency(exchangeSummary.originalItemsTotal)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#7C5DC9', display: 'block', mt: 0.5 }}
                    >
                      Crédito del intercambio:{' '}
                      {formatCurrency(exchangeSummary.paidForExchangedItems)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exchangeData.itemsToExchange.length} pieza(s)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </DashboardCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(80, 132, 235, 0.16)',
                      color: '#5084EB',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4, color: '#5084EB' }}>
                      Piezas nuevas
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: exchangeSummaryInvalid ? 'error.main' : '#5084EB',
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(exchangeSummary.newItemsTotal)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {exchangeData.newProducts.length} pieza(s)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </DashboardCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.16)',
                      color: '#10B981',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <MoneyIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4, color: '#10B981' }}>
                      Diferencia
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        color: exchangeSummaryInvalid ? 'error.main' : '#10B981',
                        lineHeight: 1.2,
                      }}
                    >
                      {formatCurrency(Math.abs(exchangeSummary.difference))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exchangeSummaryInvalid
                        ? 'Total insuficiente'
                        : exchangeSummary.difference >= 0
                          ? 'A pagar'
                          : 'Sin cargo adicional'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </DashboardCard>
          </Grid>
        </Grid>

        {exchangeSummaryInvalid && (
          <Box
            sx={{
              mb: 2.5,
              pl: 1.5,
              borderLeft: (theme) => `3px solid ${theme.palette.warning.main}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                lineHeight: 1.5,
                display: 'block',
              }}
            >
              {getExchangeCreditStep1ReminderMessage(
                exchangeSummary.paidForExchangedItems,
                formatCurrency,
              )}
            </Typography>
          </Box>
        )}

        {/* Piezas a intercambiar */}
        <S.SectionPanel>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="success" />
            Piezas a intercambiar ({exchangeData.itemsToExchange.length})
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={(theme) => ({
              border: 0,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
              boxShadow: 'none',
            })}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={(theme) => ({
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.06)
                        : theme.palette.grey[50],
                    '& th': { fontWeight: 700, color: 'text.secondary' },
                  })}
                >
                  <TableCell padding="checkbox">Seleccionar</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Precio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eligibility.map((item) => (
                  <TableRow
                    key={item.itemId}
                    hover
                    selected={exchangeData.itemsToExchange.includes(item.itemId)}
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={exchangeData.itemsToExchange.includes(item.itemId)}
                        disabled={!item.canExchange}
                        onChange={() => handleToggleItem(item.itemId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          {item.productName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Código: {item.productCode}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="700" color="text.primary">
                        {formatCurrency(item.finalPrice)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </S.SectionPanel>

        {/* Piezas nuevas */}
        <S.SectionPanel>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="success" />
            Piezas nuevas ({exchangeData.newProducts.length})
          </Typography>

          {canMixedSell && (
            <Box
              sx={(theme) => ({
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor:
                  catalogScope === 'all_branches'
                    ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                    : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                transition: 'background-color 0.2s',
              })}
            >
              <StoreIcon sx={{ fontSize: 20, color: 'info.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  ¿Habilitar piezas de otras sucursales y almacén?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.3 }}
                >
                  {catalogScope === 'all_branches'
                    ? 'Las piezas de otras sucursales o del almacén generarán una solicitud de inventario al confirmar el intercambio.'
                    : 'Solo se muestran piezas de la sucursal de la orden.'}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={catalogScope === 'all_branches'}
                onChange={(_, checked) =>
                  handleMixedSaleToggle(checked ? 'all_branches' : 'selling_branch')
                }
                inputProps={{
                  'aria-label': 'Incluir piezas de otras sucursales y almacén',
                }}
              />
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar piezas por nombre, código SKU o categoría..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Popper
              open={
                showDropdown && !!anchorEl && (filteredProducts.length > 0 || isLoadingProducts)
              }
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: 1300, width: anchorEl?.offsetWidth || '100%' }}
              modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
            >
              <Card
                sx={(theme) => ({
                  width: '100%',
                  maxHeight: 400,
                  overflow: 'auto',
                  boxShadow: 3,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                })}
                onScroll={handleDropdownScroll}
              >
                <CardContent sx={{ p: 0 }}>
                  {isLoadingProducts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const categoryName =
                        typeof product.product.category === 'string'
                          ? product.product.category
                          : product.product.category?.name || 'Sin categoría';
                      const isRemote =
                        order.location?.id != null &&
                        product.location?.id != null &&
                        product.location.id !== order.location.id;

                      return (
                        <Box
                          key={product.id}
                          onMouseDown={() => handleProductSelect(product)}
                          sx={(theme) => ({
                            p: 2,
                            cursor: 'pointer',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: getTableRowHoverOverlay(theme),
                            },
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          })}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {product.product.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                              >
                                Código: {product.productCodeUnique || 'N/A'}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mt: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: 'success.main' }}
                                >
                                  {formatCurrency(
                                    product.salePrice || product.product.salePrice || 0,
                                  )}
                                </Typography>
                                <Chip
                                  label={categoryName}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                {isRemote && (
                                  <RemoteOriginChip locationName={product.location?.name} />
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 500,
                                  color:
                                    product.availabilityStatus === 'AVAILABLE'
                                      ? 'success.main'
                                      : 'error.main',
                                  display: 'block',
                                  mb: 0.5,
                                }}
                              >
                                {product.availabilityStatus === 'AVAILABLE'
                                  ? 'Disponible'
                                  : 'No disponible'}
                              </Typography>
                              <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <SearchIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron piezas
                      </Typography>
                    </Box>
                  )}
                  {isLoadingMoreProducts && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                      <CircularProgress size={18} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Popper>
          </Box>

          {exchangeData.newProducts.length === 0 ? (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                backgroundColor: alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.14 : 0.06,
                ),
                borderRadius: '8px',
                padding: '10px 14px',
              })}
            >
              <InfoIcon
                sx={(theme) => ({
                  fontSize: 18,
                  color: theme.palette.success.main,
                  mt: '2px',
                  flexShrink: 0,
                })}
              />
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.success.light
                      : theme.palette.success.dark,
                  lineHeight: 1.5,
                })}
              >
                Busca y selecciona piezas para agregarlas al intercambio.
              </Typography>
            </Box>
          ) : (
            <Box>
              {exchangeData.newProducts.map((product) => {
                const isRemote = isRemoteCartItem(product, order.location?.id);
                return (
                  <Card
                    key={product.warehouseInventoryProduct.id}
                    sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {product.warehouseInventoryProduct.product.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}
                          >
                            Código: {product.warehouseInventoryProduct.productCodeUnique}
                          </Typography>
                          {isRemote && (
                            <Box sx={{ mb: 1 }}>
                              <RemoteOriginChip
                                locationName={product.warehouseInventoryProduct.location?.name}
                              />
                            </Box>
                          )}
                          <Typography variant="h6" sx={{ color: 'success.main' }}>
                            {formatCurrency(product.finalPrice)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemoveNewProduct(product.warehouseInventoryProduct.id)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </S.SectionPanel>

        {/* Notas del Intercambio */}
        <S.SectionPanel sx={{ mb: 0 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comentarios"
            placeholder="Motivo del intercambio, condiciones, etc."
            value={exchangeData.exchangeNotes || ''}
            onChange={(e) => onUpdateExchangeNotes(e.target.value)}
            variant="outlined"
            size="small"
            sx={(theme) => ({
              '& .MuiOutlinedInput-root': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.04)
                    : theme.palette.background.paper,
              },
            })}
          />
        </S.SectionPanel>
      </S.LeftColumn>

      <S.RightColumn>
        <S.CartHeaderBox>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ExchangeIcon color="primary" />
            Resumen ({totalSelectedPieces})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalSelectedPieces === 0
              ? 'Selecciona piezas para intercambiar'
              : totalSelectedPieces === 1
                ? '1 pieza en el intercambio'
                : `${totalSelectedPieces} piezas en el intercambio`}
          </Typography>
        </S.CartHeaderBox>

        <S.CartListBox>
          {totalSelectedPieces === 0 ? (
            <S.CartEmptyBox>
              <ExchangeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
                Sin piezas seleccionadas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Elige piezas a devolver y agrega piezas nuevas
              </Typography>
            </S.CartEmptyBox>
          ) : (
            <>
              {selectedOutgoingItems.length > 0 && (
                <Box sx={{ mb: exchangeData.newProducts.length > 0 ? 2 : 0 }}>
                  <S.SectionLabel>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      A intercambiar ({selectedOutgoingItems.length})
                    </Typography>
                  </S.SectionLabel>
                  {selectedOutgoingItems.map((item) => {
                    const product = item.orderItem?.warehouseInventoryProduct?.product;
                    return (
                      <S.CartItemCard key={item.itemId}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {product && (
                              <ProductImagePreview
                                product={getProductImageData(product as ProductImageData)}
                                alt={item.productName}
                                size={72}
                              />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}
                                  >
                                    {item.productName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                  >
                                    {item.productCode || 'N/A'}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleItem(item.itemId)}
                                  color="error"
                                  sx={{ p: 0.5, mt: -0.25, mr: -0.5, flexShrink: 0 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'primary.main', fontWeight: 600 }}
                                >
                                  {formatCurrency(item.finalPrice)}
                                </Typography>
                                <Chip
                                  label="Original"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </S.CartItemCard>
                    );
                  })}
                </Box>
              )}

              {exchangeData.newProducts.length > 0 && (
                <Box>
                  <S.SectionLabel>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Piezas nuevas ({exchangeData.newProducts.length})
                    </Typography>
                  </S.SectionLabel>
                  {exchangeData.newProducts.map((product) => {
                    const isRemote = isRemoteCartItem(product, order.location?.id);
                    const category =
                      typeof product.warehouseInventoryProduct.product.category === 'string'
                        ? product.warehouseInventoryProduct.product.category
                        : product.warehouseInventoryProduct.product.category?.name ||
                          'Sin categoría';

                    return (
                      <S.CartItemCard key={product.warehouseInventoryProduct.id}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <ProductImagePreview
                              product={getProductImageData(
                                product.warehouseInventoryProduct.product as ProductImageData,
                              )}
                              alt={product.warehouseInventoryProduct.product.name}
                              size={72}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.2 }}
                                  >
                                    {product.warehouseInventoryProduct.product.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                  >
                                    {product.warehouseInventoryProduct.productCodeUnique || 'N/A'}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    onRemoveNewProduct(product.warehouseInventoryProduct.id)
                                  }
                                  color="error"
                                  sx={{ p: 0.5, mt: -0.25, mr: -0.5, flexShrink: 0 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'success.main', fontWeight: 600 }}
                                >
                                  {formatCurrency(product.finalPrice)}
                                </Typography>
                                <Chip
                                  label={category}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                {isRemote && (
                                  <RemoteOriginChip
                                    locationName={product.warehouseInventoryProduct.location?.name}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </S.CartItemCard>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </S.CartListBox>

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <S.SummaryCard>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Piezas a intercambiar:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(exchangeSummary.originalItemsTotal)}
                </Typography>
              </Box>
              {exchangeSummary.paidForExchangedItems > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Crédito:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    −{formatCurrency(exchangeSummary.paidForExchangedItems)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Piezas nuevas:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: exchangeSummaryInvalid ? 'error.main' : 'inherit',
                  }}
                >
                  {formatCurrency(exchangeSummary.newItemsTotal)}
                </Typography>
              </Box>
              {exchangeSummary.discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="error">
                    Descuentos:
                  </Typography>
                  <Typography variant="body2" color="error">
                    −{formatCurrency(exchangeSummary.discountAmount)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Piezas:
                </Typography>
                <Typography variant="body2">
                  {totalSelectedPieces} {totalSelectedPieces === 1 ? 'pieza' : 'piezas'}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon />
                  {exchangeSummaryInvalid
                    ? 'Revisar:'
                    : exchangeSummary.difference > 0
                      ? 'A pagar:'
                      : 'Sin cargo:'}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: exchangeSummaryInvalid
                      ? 'error.main'
                      : exchangeSummary.difference > 0
                        ? 'primary.main'
                        : 'success.main',
                  }}
                >
                  {formatCurrency(Math.max(0, exchangeSummary.difference))}
                </Typography>
              </Box>
            </CardContent>
          </S.SummaryCard>
        </Box>
      </S.RightColumn>
    </S.MainContainer>
  );
};

export default ExchangeStep1Selection;
